import { useState } from 'react';
import Modal, { ModalBody, ModalFooter } from '@/components/ui/Modal';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Select from '@/components/ui/Select';
import { useMutation } from '@/hooks/useApi';
import type { Distributor } from '@/types/api';

interface AddPlatformModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface CreatePlatformPayload {
  code: string;
  distributor_name: string;
  connector_type: string;
  mode?: 'push' | 'pull' | 'hybrid';
  confirmation_method?: 'webhook' | 'poll' | 'none';
}

interface CreatePlatformResponse {
  upserted: boolean;
  distributor: Distributor;
}

const MODE_OPTIONS = [
  { value: 'push', label: 'Push - Send content to distributor' },
  { value: 'pull', label: 'Pull - Distributor fetches content' },
  { value: 'hybrid', label: 'Hybrid - Both push and pull' },
];

const CONFIRMATION_METHOD_OPTIONS = [
  { value: 'webhook', label: 'Webhook - Receive status via webhook' },
  { value: 'poll', label: 'Poll - Periodically check status' },
  { value: 'none', label: 'None - No confirmation needed' },
];

export default function AddPlatformModal({ isOpen, onClose, onSuccess }: AddPlatformModalProps) {
  const [formData, setFormData] = useState<CreatePlatformPayload>({
    code: '',
    distributor_name: '',
    connector_type: '',
    mode: 'push',
    confirmation_method: 'webhook',
  });
  const [errors, setErrors] = useState<Partial<Record<keyof CreatePlatformPayload, string>>>({});

  const { mutate, isLoading, error: apiError, reset } = useMutation<CreatePlatformResponse, CreatePlatformPayload>(
    'distribution/platforms',
    'post',
    {
      onSuccess: () => {
        handleClose();
        onSuccess();
      },
    }
  );

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof CreatePlatformPayload, string>> = {};

    if (!formData.code.trim()) {
      newErrors.code = 'Code is required';
    } else if (!/^[A-Z0-9_-]+$/i.test(formData.code)) {
      newErrors.code = 'Code must contain only letters, numbers, underscores, and hyphens';
    }

    if (!formData.distributor_name.trim()) {
      newErrors.distributor_name = 'Platform name is required';
    }

    if (!formData.connector_type.trim()) {
      newErrors.connector_type = 'Connector type is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    await mutate({
      code: formData.code.toUpperCase(),
      distributor_name: formData.distributor_name.trim(),
      connector_type: formData.connector_type.trim(),
      mode: formData.mode,
      confirmation_method: formData.confirmation_method,
    });
  };

  const handleClose = () => {
    setFormData({
      code: '',
      distributor_name: '',
      connector_type: '',
      mode: 'push',
      confirmation_method: 'webhook',
    });
    setErrors({});
    reset();
    onClose();
  };

  const handleInputChange = (field: keyof CreatePlatformPayload, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Add Distribution Platform"
      description="Create a new distributor/platform connection"
      size="md"
    >
      <form onSubmit={handleSubmit}>
        <ModalBody className="space-y-4">
          {/* API Error Display */}
          {apiError && (
            <div className="p-3 bg-danger-50 border border-danger-200 rounded-xl text-danger-700 text-sm">
              {apiError}
            </div>
          )}

          {/* Code */}
          <Input
            label="Platform Code"
            placeholder="e.g., APPLE, GOOGLE, SPOTIFY"
            value={formData.code}
            onChange={(e) => handleInputChange('code', e.target.value)}
            error={errors.code}
            hint="A unique identifier for this platform (uppercase recommended)"
          />

          {/* Distributor Name */}
          <Input
            label="Platform Name"
            placeholder="e.g., Apple Books, Google Play Books, Spotify"
            value={formData.distributor_name}
            onChange={(e) => handleInputChange('distributor_name', e.target.value)}
            error={errors.distributor_name}
            hint="The display name for this distribution platform"
          />

          {/* Connector Type */}
          <Input
            label="Connector Type"
            placeholder="e.g., apple-books, google-books, spotify, sftp, api..."
            value={formData.connector_type}
            onChange={(e) => handleInputChange('connector_type', e.target.value)}
            error={errors.connector_type}
            hint="The integration connector type (e.g., apple-books, spotify, sftp, api)"
          />

          {/* Mode */}
          <Select
            label="Distribution Mode"
            options={MODE_OPTIONS}
            value={formData.mode || 'push'}
            onChange={(e) => handleInputChange('mode', e.target.value)}
            hint="How content is delivered to this platform"
          />

          {/* Confirmation Method */}
          <Select
            label="Confirmation Method"
            options={CONFIRMATION_METHOD_OPTIONS}
            value={formData.confirmation_method || 'webhook'}
            onChange={(e) => handleInputChange('confirmation_method', e.target.value)}
            hint="How delivery status is confirmed"
          />
        </ModalBody>

        <ModalFooter>
          <Button type="button" variant="outline" onClick={handleClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button type="submit" isLoading={isLoading}>
            Add Platform
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
}

